import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_GROUPS = ["U8", "U10", "U12", "U14", "U16", "U18", "Senior"];
const POSITIONS = [
  "Goalkeeper",
  "Defender",
  "Midfielder",
  "Forward",
  "Undecided",
];

const initialState = {
  player_first_name: "",
  player_last_name: "",
  date_of_birth: "",
  age_group: "",
  position: "",
  parent_name: "",
  parent_email: "",
  parent_phone: "",
  address: "",
  experience: "",
  medical_notes: "",
};

export default function Registration() {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((s) => ({ ...s, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.player_first_name ||
      !form.player_last_name ||
      !form.date_of_birth ||
      !form.age_group ||
      !form.parent_name ||
      !form.parent_email ||
      !form.parent_phone
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/registrations`, form);
      setSuccess(true);
      toast.success("Registration received! We'll be in touch within 48 hours.");
      setForm(initialState);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="register"
      data-testid="registration-section"
      className="relative py-24 lg:py-36 px-6 lg:px-10 bg-neutral-950 border-y border-white/10"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 fade-up">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-red-600" />
            <span className="text-xs uppercase tracking-[0.3em] text-red-600">
              Join the Academy
            </span>
          </div>
          <h2
            data-testid="register-title"
            className="font-heading uppercase text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight mb-8"
          >
            Take Your <br />
            <span className="text-red-600">First Step.</span>
          </h2>
          <p className="text-white/70 leading-relaxed mb-10 max-w-md">
            Submit the registration below and one of our coaches will reach out
            to schedule a free trial session and discuss program fit.
          </p>

          <ul className="space-y-4 max-w-md">
            {[
              "Free trial session for new players",
              "Tailored development plan per age group",
              "Two pitch sessions + one match per week",
              "Quarterly progress reviews with parents",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3 text-sm text-white/80">
                <CheckCircle2 className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                {b}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7 fade-up">
          <form
            onSubmit={handleSubmit}
            data-testid="registration-form"
            className="rounded-2xl border border-white/10 bg-black/40 p-6 lg:p-10 space-y-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="Player First Name *"
                id="player_first_name"
                value={form.player_first_name}
                onChange={handleChange("player_first_name")}
                placeholder="Lebo"
              />
              <Field
                label="Player Last Name *"
                id="player_last_name"
                value={form.player_last_name}
                onChange={handleChange("player_last_name")}
                placeholder="Mahlangu"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="Date of Birth *"
                id="date_of_birth"
                type="date"
                value={form.date_of_birth}
                onChange={handleChange("date_of_birth")}
              />

              <div className="space-y-2">
                <Label
                  htmlFor="age_group"
                  className="text-xs uppercase tracking-widest text-white/60"
                >
                  Age Group *
                </Label>
                <Select
                  value={form.age_group}
                  onValueChange={(v) => setForm((s) => ({ ...s, age_group: v }))}
                >
                  <SelectTrigger
                    id="age_group"
                    data-testid="register-age-group"
                    className="bg-white/5 border-white/10 text-white focus:ring-2 focus:ring-red-600 h-11"
                  >
                    <SelectValue placeholder="Select age group" />
                  </SelectTrigger>
                  <SelectContent className="bg-neutral-900 border-white/10 text-white">
                    {AGE_GROUPS.map((a) => (
                      <SelectItem
                        key={a}
                        value={a}
                        className="focus:bg-red-600 focus:text-white"
                      >
                        {a}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="position"
                className="text-xs uppercase tracking-widest text-white/60"
              >
                Preferred Position
              </Label>
              <Select
                value={form.position}
                onValueChange={(v) => setForm((s) => ({ ...s, position: v }))}
              >
                <SelectTrigger
                  id="position"
                  data-testid="register-position"
                  className="bg-white/5 border-white/10 text-white focus:ring-2 focus:ring-red-600 h-11"
                >
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent className="bg-neutral-900 border-white/10 text-white">
                  {POSITIONS.map((p) => (
                    <SelectItem
                      key={p}
                      value={p}
                      className="focus:bg-red-600 focus:text-white"
                    >
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="h-px w-full bg-white/10 my-2" />

            <Field
              label="Parent / Guardian Name *"
              id="parent_name"
              value={form.parent_name}
              onChange={handleChange("parent_name")}
              placeholder="Thandi Mahlangu"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="Email *"
                id="parent_email"
                type="email"
                value={form.parent_email}
                onChange={handleChange("parent_email")}
                placeholder="parent@email.com"
              />
              <Field
                label="Phone *"
                id="parent_phone"
                type="tel"
                value={form.parent_phone}
                onChange={handleChange("parent_phone")}
                placeholder="+27 ..."
              />
            </div>

            <Field
              label="Address"
              id="address"
              value={form.address}
              onChange={handleChange("address")}
              placeholder="Witbank suburb"
            />

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-white/60">
                Previous Experience
              </Label>
              <Textarea
                data-testid="register-experience"
                value={form.experience}
                onChange={handleChange("experience")}
                rows={3}
                placeholder="Years played, clubs, school teams..."
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-red-600"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-white/60">
                Medical Notes
              </Label>
              <Textarea
                data-testid="register-medical"
                value={form.medical_notes}
                onChange={handleChange("medical_notes")}
                rows={2}
                placeholder="Allergies, injuries or conditions we should know about..."
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-red-600"
              />
            </div>

            <Button
              data-testid="register-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-500 text-white font-heading uppercase tracking-widest rounded-full h-14 text-sm"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Submitting
                </>
              ) : success ? (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Submit Another
                </>
              ) : (
                <>
                  Submit Registration <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>

            <p className="text-[11px] text-white/40 text-center">
              By submitting, you agree to be contacted by Stars of Mpumalanga
              regarding your enrollment.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, id, type = "text", value, onChange, placeholder }) {
  return (
    <div className="space-y-2">
      <Label
        htmlFor={id}
        className="text-xs uppercase tracking-widest text-white/60"
      >
        {label}
      </Label>
      <Input
        id={id}
        data-testid={`register-${id}`}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-red-600 h-11"
      />
    </div>
  );
}
